import * as CONSTANTS from '../../shared/constants';

const { GRID_SIZE, PLAYER_RADIUS_X, PLAYER_RADIUS_Y, RENDER_DELAY, ANIMATION_TIMEOUT } = CONSTANTS;

export const renderPlayers = (
  ctx,
  players,
  myPlayerId,
  playerStateBuffers,
  interpolatePosition,
  playerAnimationState,
  characterSprites,
  characterSpriteSVGs,
  coloredSpritesCache,
  createColoredSprite,
  drawRef
) => {
  players.forEach((player, playerId) => {
    if (player.alive) {
      // Use delayed interpolation for other players, actual position for our player
      let renderX = player.x;
      let renderY = player.y;
      
      if (player.playerId !== myPlayerId) {
        const buffer = playerStateBuffers.current.get(player.playerId);
        if (buffer && buffer.length >= 1) {
          const now = performance.now();
          const renderTime = now - RENDER_DELAY;
          const interpolatedPos = interpolatePosition(buffer, renderTime, now, false);
          
          if (interpolatedPos) {
            renderX = interpolatedPos.x;
            renderY = interpolatedPos.y;
          }
        }
      }
      
      const playerCenterX = renderX + GRID_SIZE / 2;
      const playerCenterY = renderY + GRID_SIZE / 2;
      
      // Draw black shadow oval - moved up to be more centered on tile
      ctx.fillStyle = '#000000';
      ctx.beginPath();
      ctx.ellipse(playerCenterX, playerCenterY + GRID_SIZE * 0.1, PLAYER_RADIUS_X, PLAYER_RADIUS_Y, 0, 0, 2 * Math.PI);
      ctx.fill();
      
      // Use server-provided animation state
      let animState = {
        direction: player.direction || 'front',
        frame: player.frame || 0
      };
      
      // Fallback for old animation state (will be removed)
      if (!animState.direction) {
        const oldState = playerAnimationState.current.get(playerId);
        if (oldState) {
          animState = oldState;
        }
      }
      
      if (!animState) {
        // Initialize animation state for new players
        animState = { direction: 'front', frame: 0, lastMoveTime: 0, lastFrameChangeTime: 0 };
        playerAnimationState.current.set(playerId, animState);
      }
      
      // Reset to standing frame if no animation update for a while (for other players)
      const now = Date.now();
      if (playerId !== myPlayerId && animState.frame !== 0 && now - animState.lastFrameChangeTime > ANIMATION_TIMEOUT) {
        animState.frame = 0;
      }
      
      // Draw character sprite if loaded
      if (characterSpriteSVGs && characterSpriteSVGs[animState.direction] && characterSpriteSVGs[animState.direction][animState.frame]) {
        // Get or create colored sprite (using normal player color)
        const cacheKey = `${player.color}-${animState.direction}-${animState.frame}`;
        let sprite = coloredSpritesCache.current.get(cacheKey);
        
        if (!sprite) {
          // Create colored version from SVG text and cache it
          const svgText = characterSpriteSVGs[animState.direction][animState.frame];
          sprite = createColoredSprite(svgText, player.color);
          if (sprite) {
            coloredSpritesCache.current.set(cacheKey, sprite);
          } else {
            // Fallback to original sprite if coloring fails
            sprite = characterSprites[animState.direction] && characterSprites[animState.direction][animState.frame];
          }
        }
        
        // Check if the colored sprite is loaded
        if (sprite && sprite.complete) {
          const spriteHeight = GRID_SIZE * 1.5; // 1.5 tiles tall
          const spriteWidth = spriteHeight * 0.75; // Keep aspect ratio
          const spriteX = playerCenterX - spriteWidth / 2;
          const spriteY = playerCenterY - spriteHeight * 0.85; // Position character higher up on tile
          
          // Draw the normal sprite first
          ctx.drawImage(sprite, spriteX, spriteY, spriteWidth, spriteHeight);
          
          // Add disease effect as a simple overlay (much more performant)
          if (player.diseases && Object.keys(player.diseases).length > 0) {
            const now = Date.now();
            let hasDiseases = false;
            
            // Check if player has any active diseases
            for (const [, expiryTime] of Object.entries(player.diseases)) {
              if (now < expiryTime) {
                hasDiseases = true;
                break;
              }
            }
            
            if (hasDiseases) {
              // Simple oscillating dark overlay - much more performant than color mixing
              const oscillation = Math.sin(now * 0.015) * 0.5 + 0.5; // 0 to 1
              const overlayOpacity = oscillation * 0.6; // Max 60% dark overlay
              
              ctx.save();
              ctx.fillStyle = `rgba(0, 0, 0, ${overlayOpacity})`;
              ctx.fillRect(spriteX, spriteY, spriteWidth, spriteHeight);
              ctx.restore();
            }
          }
        } else if (sprite) {
          // If sprite is still loading, wait for it
          sprite.onload = () => {
            // Trigger a redraw when the sprite loads
            if (drawRef.current) drawRef.current();
          };
          // Fallback to original sprite for this frame
          if (characterSprites[animState.direction] && characterSprites[animState.direction][animState.frame]) {
            const originalSprite = characterSprites[animState.direction][animState.frame];
            const spriteHeight = GRID_SIZE * 1.5;
            const spriteWidth = spriteHeight * 0.75;
            const spriteX = playerCenterX - spriteWidth / 2;
            const spriteY = playerCenterY - spriteHeight * 0.85;
            ctx.drawImage(originalSprite, spriteX, spriteY, spriteWidth, spriteHeight);
          }
        }
      } else {
        // Fallback to colored oval if sprites not loaded
        ctx.fillStyle = player.color;
        ctx.beginPath();
        ctx.ellipse(playerCenterX, playerCenterY, PLAYER_RADIUS_X, PLAYER_RADIUS_Y, 0, 0, 2 * Math.PI);
        ctx.fill();
      }

      // Player ID indicator
      ctx.fillStyle = '#fff';
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      const displayName = player.nickname || `P${playerId + 1}`;
      ctx.fillText(displayName, playerCenterX, playerCenterY - GRID_SIZE * 0.9);
      
      // Show disconnected status
      if (player.connected === false) {
        ctx.fillStyle = 'rgba(255, 0, 0, 0.7)';
        ctx.font = 'bold 10px Arial';
        ctx.fillText('DISCONNECTED', playerCenterX, playerCenterY - GRID_SIZE * 0.7);
        
        // Add semi-transparent overlay
        ctx.fillStyle = 'rgba(128, 128, 128, 0.5)';
        ctx.beginPath();
        ctx.ellipse(playerCenterX, playerCenterY, PLAYER_RADIUS_X, PLAYER_RADIUS_Y, 0, 0, 2 * Math.PI);
        ctx.fill();
      }
    }
  });
};